import { useQuery } from '@tanstack/react-query'
import { pingAPI } from '@/lib/ping-api'
import { queryKeys } from '@/lib/query-keys'
import type { InternalChatListParams, InternalChatUserListParams, InternalChatDetail } from '@/types'
import { useAuthStore } from '@/stores/authStore'

// Hook for fetching paginated list of internal chats
export const useInternalChats = (params?: InternalChatListParams, enabled: boolean = true) => {
  const { user } = useAuthStore()
  return useQuery({
    queryKey: queryKeys.ping.internalChats.list(params),
    queryFn: async () => {
      try {
        return await pingAPI.internalChats.list(params)
      } catch (error: any) {
        // Handle 404 errors gracefully - return empty paginated response
        if (error?.status === 404) {
          return { results: [], count: 0, next: null, previous: null }
        }
        throw error
      }
    },
    select: (data) => data.results || [],
    retry: false,
    staleTime: Infinity, // Never go stale - rely on real-time updates for freshness
    gcTime: 300000, // 5 minutes
    enabled: !!user && enabled,
  })
}

// Hook for fetching available users to start internal chats with
export const useAvailableInternalChatUsers = (params?: InternalChatUserListParams) => {
  const { user } = useAuthStore()
  return useQuery({
    queryKey: queryKeys.ping.internalChatUsers.list(params),
    queryFn: async () => {
      try {
        return await pingAPI.internalChats.listAvailableUsers(params)
      } catch (error: any) {
        // Handle 404 errors gracefully - return empty paginated response
        if (error?.status === 404) {
          return { results: [], count: 0, next: null, previous: null }
        }
        throw error
      }
    },
    select: (data) => data.results || [],
    retry: false,
    staleTime: 60000, // 1 minute - users list can change
    gcTime: 300000, // 5 minutes
    enabled: !!user,
  })
}

// Hook for fetching a single internal chat detail
export const useInternalChatDetail = (chatId: string, enabled: boolean = true) => {
  const query = useQuery({
    queryKey: queryKeys.ping.internalChats.detail(chatId),
    queryFn: () => pingAPI.internalChats.get(chatId),
    select: (data: InternalChatDetail) => data,
    enabled: !!chatId && enabled,
    retry: false,
    staleTime: Infinity, // Never go stale - rely on real-time updates
  })
  return query
}

